/**
 * LOCO — motion.
 * Reveal on scroll, light parallax, and the split-flap hero statement.
 * Everything here is optional: content is fully visible without it,
 * and prefers-reduced-motion switches every effect off.
 */
(() => {
  const LOCO = (window.LOCO = window.LOCO || {});
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  // Hide [data-reveal] content only once this script is actually running
  if (!reduce.matches) document.documentElement.classList.add('reveal-ready');

  /* ---------- Reveal ---------- */
  function initReveal(root = document) {
    const els = root.querySelectorAll('[data-reveal]:not(.is-revealed)');
    if (!els.length) return;
    if (reduce.matches || !('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-revealed'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );
    els.forEach((el, i) => {
      if (el.dataset.revealStagger !== undefined) el.style.setProperty('--reveal-delay', `${(i % 4) * 70}ms`);
      io.observe(el);
    });
  }

  /* ---------- Parallax (transform only, rAF-throttled, visible elements only) ---------- */
  function initParallax() {
    if (reduce.matches) return;
    const els = [...document.querySelectorAll('[data-parallax]')];
    if (!els.length) return;
    const visible = new Set();
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => (e.isIntersecting ? visible.add(e.target) : visible.delete(e.target)));
    });
    els.forEach((el) => io.observe(el));

    let ticking = false;
    const update = () => {
      const vh = window.innerHeight;
      visible.forEach((el) => {
        const rect = el.parentElement.getBoundingClientRect();
        const progress = (rect.top + rect.height / 2 - vh / 2) / (vh + rect.height); // -0.5 … 0.5
        const amount = Number(el.dataset.parallax) || 10;
        el.style.transform = `translate3d(0, ${(progress * amount).toFixed(2)}%, 0)`;
      });
      ticking = false;
    };
    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(update);
        }
      },
      { passive: true }
    );
    update();
  }

  /* ---------- Split-flap statement ---------- */
  class LocoFlip extends HTMLElement {
    connectedCallback() {
      const next = this.nextElementSibling;
      this.toggle = next && next.matches('[data-flip-toggle]') ? next : null;
      this.mode = this.dataset.mode || 'flip';
      if (this.mode !== 'flip' || reduce.matches) {
        this.classList.add('flip--static');
        if (this.toggle) this.toggle.hidden = true;
        return;
      }
      this.paused = false;
      this.classList.add('is-running');
      if (this.toggle) {
        this.toggle.hidden = false;
        this.toggle.addEventListener('click', () => this.setPaused(!this.paused, true));
      }
      // Pause while off-screen or when the tab is hidden (saves battery, avoids distraction)
      this.io = new IntersectionObserver(([entry]) => {
        if (!this.userPaused) this.setPaused(!entry.isIntersecting);
      });
      this.io.observe(this);
      document.addEventListener('visibilitychange', () => {
        if (!this.userPaused) this.setPaused(document.hidden);
      });
    }

    setPaused(paused, byUser = false) {
      this.paused = paused;
      if (byUser) this.userPaused = paused;
      this.classList.toggle('is-paused', paused);
      if (this.toggle && byUser) {
        this.toggle.setAttribute('aria-pressed', String(paused));
        const label = this.toggle.querySelector('[data-flip-toggle-label]');
        if (label) label.textContent = paused ? this.toggle.dataset.playLabel : this.toggle.dataset.pauseLabel;
      }
    }

    disconnectedCallback() {
      this.io?.disconnect();
    }
  }
  if (!customElements.get('loco-flip')) customElements.define('loco-flip', LocoFlip);

  function init() {
    initReveal();
    initParallax();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Content injected later (recommendations, Theme Editor section reloads)
  document.addEventListener('loco:content-loaded', (event) => initReveal(event.target));
  document.addEventListener('shopify:section:load', (event) => initReveal(event.target));
  LOCO.initReveal = initReveal;
})();
